import { Client } from '@stomp/stompjs';
import { writable, get } from 'svelte/store';
import { selectedWeek } from './AppStatus';
import { endPointUrl } from './AppConfig';

export const messages = writable([]);
export const connectionStatus = writable('Disconnected');

let client;
let isConnected = false;

function buildWebSocketUrl(apiUrl) {
    const trimmedUrl = String(apiUrl || '').trim();

    if (!trimmedUrl) {
        return null;
    }

    if (trimmedUrl.startsWith('ws://') || trimmedUrl.startsWith('wss://')) {
        return trimmedUrl.endsWith('/') ? `${trimmedUrl}msg` : `${trimmedUrl}/msg`;
    }

    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        const wsBaseUrl = trimmedUrl.replace(/^http/, 'ws');
        return wsBaseUrl.endsWith('/') ? `${wsBaseUrl}msg` : `${wsBaseUrl}/msg`;
    }

    if (trimmedUrl.startsWith('/')) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}${trimmedUrl.endsWith('/') ? trimmedUrl : `${trimmedUrl}/`}msg`;
    }

    return null;
}

export function wsConnect() {
    if (isConnected || client?.active) return;

    const unsubscribe = endPointUrl.subscribe((url) => {
        const apiUrl = import.meta.env.VITE_PROXY_API_URL || url;

        if (!apiUrl) {
            console.log('Waiting for a valid API URL...');
            return;
        }

        const wsUrl = buildWebSocketUrl(apiUrl);

        if (!wsUrl || !(wsUrl.startsWith('ws://') || wsUrl.startsWith('wss://'))) {
            console.error('Invalid WebSocket URL.', { apiUrl, wsUrl });
            connectionStatus.set('Disconnected');
            isConnected = false;
            return;
        }

        console.log({ wsUrl });

        try {
            client = new Client({
                brokerURL: wsUrl,
                reconnectDelay: 5000,
                heartbeatIncoming: 4000,
                heartbeatOutgoing: 4000,
                onConnect: () => {
                    connectionStatus.set('Connected');
                    isConnected = true;
                    subscribeToTopics();

                    if (unsubscribe && typeof unsubscribe === 'function') {
                        unsubscribe();
                    }
                },
                onDisconnect: () => {
                    connectionStatus.set('Disconnected');
                    isConnected = false;
                },
                onStompError: (error) => {
                    console.error('STOMP error: ', error);
                    connectionStatus.set('Disconnected');
                    isConnected = false;
                },
                onWebSocketError: (error) => {
                    console.error('WebSocket error: ', error);
                    connectionStatus.set('Disconnected');
                    isConnected = false;
                },
                onWebSocketClose: (event) => {
                    console.warn('WebSocket closed: ', event);
                    connectionStatus.set('Disconnected');
                    isConnected = false;
                },
            });

            client.activate();
        } catch (error) {
            console.error('WebSocket client initialization failed: ', error);
            connectionStatus.set('Disconnected');
            isConnected = false;
        }
    });
}

function subscribeToTopics() {
    if (!client || !isConnected) {
        console.warn('WebSocket client is not connected. Subscriptions skipped.');
        return;
    }

    console.log('Subscribing to WebSocket topics...');
    client.subscribe('/topic/messages', handleMessage);
    client.subscribe('/topic/weekStatus', handleMessage);
 //   client.subscribe('/topic/exchangeRate', handleMessage);
}

function handleMessage(message) {
    try {
        const parsedMsg = JSON.parse(message.body);

        if (message.headers.destination === '/topic/weekStatus') {
            handleWeekStatusMessage(parsedMsg);
        }

        messages.update((m) => [...m, parsedMsg]);
    } catch (error) {
        console.error('Failed to process WebSocket message: ', error, message);
    }
}

function handleWeekStatusMessage(msg) {
    const currentWeek = get(selectedWeek);

    if (currentWeek.id !== msg.data.id || currentWeek.status !== msg.data.status) {
        selectedWeek.update(week => ({
            ...week,
            id: msg.data.id,
            status: msg.data.status
        }));
    }
}

export function sendMessage(text, data = {}) {
    if (!client || !isConnected) {
        console.warn('WebSocket client is not connected. Message was not sent.');
        return false;
    }

    const message = {
        from: 'User',
        text,
        data,
    };

    try {
        client.publish({ destination: '/app/msg', body: JSON.stringify(message) });
        return true;
    } catch (error) {
        console.error('Failed to send WebSocket message: ', error);
        return false;
    }
}
